import { useState, useRef, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { Send, Loader2, Scale, ChevronDown, ChevronUp, Copy, Check, Share2, Mail, MessageSquare, LogOut, User, CreditCard, Settings, Clock, AlertCircle, CheckCircle, XCircle, DollarSign, Shield, FileText, History, Plus, Trash2, Download, Moon, Sun, Menu, X, Upload, File, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Toaster, toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PricingPage from "@/pages/PricingPage";
import AdminDashboard from "@/pages/AdminDashboard";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EXPENSE_JUSTIFICATION_URL = "https://customer-assets.emergentagent.com/job_healthcare-ops/artifacts/i2tltswd_OpsCounsel_AI_Expense_Justification.pdf";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_opscounsel-ai/artifacts/a8v8akpl_F86DF7B3-E743-4829-B779-387ACA6F68DC_L0_001%20-%20Edited.png";

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

// API helpers
const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============ Components ============

// Auth Modal Component
const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const response = await api.post(endpoint, { email, password });
      localStorage.setItem("token", response.data.token);
      onSuccess(response.data.user);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {isLogin ? "Sign In" : "Create Account"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {isLogin 
              ? "Sign in to continue using OpsCounsel™ AI" 
              : "Create an account to get started"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-background border-border"
              required
              data-testid="auth-email"
            />
          </div>
          <div>
            <Input
              type="password"
              placeholder="Password (min 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-background border-border"
              minLength={6}
              required
              data-testid="auth-password"
            />
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90"
            disabled={loading}
            data-testid="auth-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (isLogin ? "Sign In" : "Create Account")}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <button
              type="button"
              className="ml-1 text-primary hover:underline"
              onClick={() => setIsLogin(!isLogin)}
            >
              {isLogin ? "Sign up" : "Sign in"}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Guest Signup Prompt Component - shown after 3 free guest messages
const GuestSignupPrompt = ({ isOpen, onClose, onSignup, guestMessagesUsed, onEmailCapture }) => {
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setSubmitting(true);
    try {
      await onEmailCapture(email);
      setEmailSubmitted(true);
    } catch (err) {
      console.error("Email capture failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full bg-primary/10">
              <MessageSquare className="w-8 h-8 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-xl font-semibold text-center">
            You're getting great insights!
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-center mt-2">
            You've used {guestMessagesUsed} of 3 preview messages.
            <br />
            <span className="text-foreground font-medium">Sign up free</span> to unlock 7 more messages and save your conversation history.
          </DialogDescription>
        </DialogHeader>

        {!emailSubmitted ? (
          <>
            {/* Email capture form */}
            <form onSubmit={handleEmailSubmit} className="mt-4">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1"
                  data-testid="guest-email-input"
                />
                <Button type="submit" disabled={submitting || !email.trim()} data-testid="guest-email-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                We'll save your conversation and send you tips
              </p>
            </form>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border/50"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={onSignup}
                data-testid="guest-signup-btn"
              >
                Create Free Account
              </Button>
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={onClose}
                data-testid="guest-continue-btn"
              >
                Maybe Later
              </Button>
            </div>
          </>
        ) : (
          <div className="mt-4 text-center">
            <div className="flex justify-center mb-3">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <p className="text-foreground font-medium mb-2">Thanks! We've saved your email.</p>
            <p className="text-sm text-muted-foreground mb-4">
              Continue chatting or create an account to unlock more features.
            </p>
            <div className="space-y-3">
              <Button className="w-full" onClick={onSignup}>
                Create Free Account
              </Button>
              <Button variant="ghost" className="w-full text-muted-foreground" onClick={onClose}>
                Continue Chatting
              </Button>
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-border/50">
          <p className="text-xs text-muted-foreground text-center">
            Already have an account?{" "}
            <button 
              onClick={onSignup}
              className="text-primary hover:underline font-medium"
            >
              Sign in
            </button>
          </p>
        </div>

        {/* KARG Handoff CTA */}
        <div className="mt-3 text-center">
          <p className="text-xs text-muted-foreground">
            Need hands-on support instead?{" "}
            <a 
              href="https://key-assets.com/inquiry" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary/80 hover:text-primary hover:underline"
            >
              Talk to a KARG operator →
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Paywall Modal Component
const PaywallModal = ({ isOpen, onClose, onSelectPlan }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get("/subscription/plans");
        setPlans(response.data.plans);
      } catch (err) {
        console.error("Failed to fetch plans:", err);
      }
    };
    if (isOpen) fetchPlans();
  }, [isOpen]);

  const handleSelectPlan = async (planId) => {
    setSelectedPlan(planId);
    setLoading(true);
    try {
      await onSelectPlan(planId);
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">
            You've reached the free usage limit.
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-center">
            OpsCounsel™ AI provides executive-level operational judgment for ASC leaders.
            <br />Upgrade to continue using OpsCounsel™.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative p-5 rounded-lg border transition-all ${
                plan.badge 
                  ? "border-primary bg-primary/5" 
                  : "border-border bg-background hover:border-muted-foreground/50"
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-full font-medium">
                  {plan.badge}
                </div>
              )}
              <h3 className="font-semibold text-foreground mt-2">{plan.name}</h3>
              <div className="mt-2">
                <span className="text-2xl font-bold text-foreground">${plan.price}</span>
                <span className="text-muted-foreground text-sm">
                  {plan.interval === "month" && "/mo"}
                  {plan.interval === "year" && "/yr"}
                  {plan.interval === "one_time" && ""}
                </span>
              </div>
              {plan.savings && (
                <div className="mt-1">
                  <span className="text-xs font-semibold text-[#C9A24D] bg-[#C9A24D]/10 px-2 py-0.5 rounded">
                    {plan.savings}
                  </span>
                </div>
              )}
              {plan.monthly_equivalent && (
                <p className="text-xs text-muted-foreground mt-1">
                  Just ${plan.monthly_equivalent}/mo
                </p>
              )}
              {plan.duration && (
                <p className="text-xs text-muted-foreground mt-1">{plan.duration} access</p>
              )}
              <p className="text-xs text-muted-foreground mt-3">{plan.description}</p>
              <Button
                className="w-full mt-4"
                variant={plan.badge ? "default" : "outline"}
                onClick={() => handleSelectPlan(plan.id)}
                disabled={loading}
                data-testid={`plan-${plan.id}`}
              >
                {loading && selectedPlan === plan.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  plan.interval === "one_time" ? "Get Pass" : "Upgrade"
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* Money-back guarantee */}
        <div className="mt-4 text-center">
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
            <Shield className="w-3.5 h-3.5 text-[#C9A24D]" />
            14-day money-back guarantee. Cancel anytime.
          </p>
        </div>

        {/* Expense Justification */}
        <div className="mt-4 pt-4 border-t border-border/50 text-center">
          <p className="text-xs text-muted-foreground mb-2">
            Subscriptions are commonly reimbursed as professional development or decision-support software.
          </p>
          <a 
            href={EXPENSE_JUSTIFICATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
            data-testid="paywall-expense-link"
          >
            <FileText className="w-3.5 h-3.5" />
            Download expense justification
          </a>
        </div>

        {/* KARG Handoff CTA */}
        <div className="mt-3 text-center">
          <p className="text-xs text-muted-foreground">
            Need hands-on support instead?{" "}
            <a 
              href="https://key-assets.com/inquiry" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary/80 hover:text-primary hover:underline"
            >
              Talk to a KARG operator →
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Message Counter Component
const MessageCounter = ({ used, remaining, status }) => {
  if (status !== "free") return null;
  
  return (
    <div className="text-xs text-muted-foreground text-center py-2 border-b border-border/50">
      {remaining > 0 
        ? `${remaining} of 7 free messages remaining`
        : "Free messages exhausted"}
    </div>
  );
};

// Guest Message Counter Component
const GuestMessageCounter = ({ guestMessagesUsed, limit }) => {
  const remaining = limit - guestMessagesUsed;
  
  return (
    <div className="text-xs text-center py-2 border-b border-border/50">
      <span className="text-muted-foreground">
        {remaining > 0 
          ? `${remaining} of ${limit} preview messages remaining • `
          : "Preview messages exhausted • "}
      </span>
      <span className="text-primary font-medium">Sign up for 7 more free</span>
    </div>
  );
};

// Customer Portal Component
const CustomerPortal = ({ isOpen, onClose, onUpgrade, onRefreshUser }) => {
  const [details, setDetails] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [upgrading, setUpgrading] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchDetails();
      fetchHistory();
    }
  }, [isOpen]);

  const fetchDetails = async () => {
    try {
      const response = await api.get("/subscription/details");
      setDetails(response.data);
    } catch (err) {
      console.error("Failed to fetch subscription details:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get("/subscription/history");
      setHistory(response.data.history || []);
    } catch (err) {
      console.error("Failed to fetch history:", err);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription cancelled. You'll retain access until the end of your billing period.");
      setShowCancelConfirm(false);
      fetchDetails();
      onRefreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(planId);
    try {
      await onUpgrade(planId);
    } finally {
      setUpgrading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "monthly":
      case "annual":
      case "survey_pass":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "expired":
        return <XCircle className="w-5 h-5 text-destructive" />;
      default:
        return <AlertCircle className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "monthly": return "Monthly Subscription";
      case "annual": return "Annual Subscription";
      case "survey_pass": return "Survey Readiness Pass";
      case "expired": return "Expired";
      default: return "Free Plan";
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Subscription Management
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Manage your OpsCounsel AI subscription and billing
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : details ? (
            <div className="space-y-6 mt-4">
              {/* Current Plan */}
              <div className="p-5 rounded-lg border border-border bg-background">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(details.subscription?.status)}
                    <div>
                      <h3 className="font-semibold text-foreground">
                        {details.subscription?.plan_name || "Free Plan"}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {getStatusLabel(details.subscription?.status)}
                      </p>
                    </div>
                  </div>
                  {details.subscription?.price && (
                    <div className="text-right">
                      <span className="text-2xl font-bold text-foreground">${details.subscription.price}</span>
                      <span className="text-muted-foreground text-sm">
                        {details.subscription.interval === "month" && "/mo"}
                        {details.subscription.interval === "year" && "/yr"}
                      </span>
                    </div>
                  )}
                </div>

                {details.subscription?.expires_at && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {details.subscription?.is_cancelled 
                          ? "Access ends: " 
                          : details.subscription?.status === "survey_pass"
                            ? "Pass expires: "
                            : "Renews: "}
                        {formatDate(details.subscription.expires_at)}
                        {details.subscription?.days_remaining !== null && (
                          <span className="ml-1">({details.subscription.days_remaining} days remaining)</span>
                        )}
                      </span>
                    </div>
                    {details.subscription?.is_cancelled && (
                      <p className="text-xs text-destructive mt-2">
                        Subscription cancelled on {formatDate(details.subscription.cancelled_at)}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Usage Stats */}
              <div className="p-5 rounded-lg border border-border bg-background">
                <h4 className="font-medium text-foreground mb-3">Usage</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-2xl font-bold text-foreground">{details.usage?.messages_used || 0}</p>
                    <p className="text-xs text-muted-foreground">Messages sent</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {details.usage?.messages_remaining === "Unlimited" 
                        ? "Unlimited" 
                        : details.usage?.messages_remaining || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Messages remaining</p>
                  </div>
                </div>
              </div>

              {/* Upgrade Options */}
              {details.available_upgrades && details.available_upgrades.length > 0 && (
                <div className="p-5 rounded-lg border border-border bg-background">
                  <h4 className="font-medium text-foreground mb-3">
                    {details.subscription?.status === "free" ? "Upgrade Your Plan" : "Available Upgrades"}
                  </h4>
                  <div className="space-y-2">
                    {details.available_upgrades.map((upgrade) => (
                      <div 
                        key={upgrade.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-foreground">{upgrade.name}</p>
                          <p className="text-sm text-muted-foreground">${upgrade.price}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleUpgrade(upgrade.id)}
                          disabled={upgrading === upgrade.id}
                          data-testid={`upgrade-${upgrade.id}`}
                        >
                          {upgrading === upgrade.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            "Upgrade"
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment History */}
              {history.length > 0 && (
                <div className="p-5 rounded-lg border border-border bg-background">
                  <h4 className="font-medium text-foreground mb-3">Payment History</h4>
                  <div className="space-y-2">
                    {history.slice(0, 5).map((tx, idx) => (
                      <div 
                        key={tx.id || idx}
                        className="flex items-center justify-between py-2 border-b border-border/30 last:border-0"
                      >
                        <div>
                          <p className="text-sm text-foreground">{tx.plan}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(tx.date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-foreground">${tx.amount}</p>
                          <p className={`text-xs ${tx.status === "paid" ? "text-green-500" : "text-muted-foreground"}`}>
                            {tx.status}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cancel Subscription */}
              {details.subscription?.status !== "free" && 
               details.subscription?.status !== "expired" && 
               !details.subscription?.is_cancelled && (
                <div className="pt-4 border-t border-border">
                  <Button
                    variant="ghost"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setShowCancelConfirm(true)}
                    data-testid="cancel-subscription-btn"
                  >
                    Cancel Subscription
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">Failed to load subscription details</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll retain access until the end of your current billing period. 
              After that, you'll be limited to the free tier (7 messages total).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-destructive hover:bg-destructive/90"
              disabled={cancelling}
            >
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, Cancel"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// User Menu Component
const UserMenu = ({ user, onLogout, onOpenPortal, onOpenAdmin, onOpenDocuments }) => {
  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted/50 transition-colors" data-testid="user-menu">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
            <User className="w-4 h-4 text-primary" />
          </div>
          <span className="text-sm text-foreground hidden md:inline">{user.email}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-card border-border w-56">
        <div className="px-3 py-2">
          <p className="text-sm font-medium text-foreground">{user.email}</p>
          <p className="text-xs text-muted-foreground capitalize">
            {user.subscription_status === "free" 
              ? `Free (${Math.max(0, 7 - user.messages_used)}/7 remaining)`
              : user.subscription_status === "monthly" 
                ? "Monthly Subscriber"
                : user.subscription_status === "annual"
                  ? "Annual Subscriber"
                  : user.subscription_status === "survey_pass"
                    ? "Survey Pass"
                    : user.subscription_status}
          </p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          onClick={onOpenDocuments}
          className="cursor-pointer"
          data-testid="documents-btn"
        >
          <FolderOpen className="w-4 h-4 mr-2" />
          My Documents
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={onOpenPortal}
          className="cursor-pointer"
          data-testid="subscription-btn"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          Subscription
        </DropdownMenuItem>
        {user.is_admin && (
          <DropdownMenuItem 
            onClick={onOpenAdmin}
            className="cursor-pointer"
            data-testid="admin-btn"
          >
            <Shield className="w-4 h-4 mr-2" />
            Admin Dashboard
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          asChild
          className="cursor-pointer text-muted-foreground"
        >
          <a 
            href="https://key-assets.com/inquiry" 
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="karg-services-btn"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Need Human Help? → KARG
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          onClick={onLogout}
          className="cursor-pointer text-destructive focus:text-destructive"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Documents Modal Component
const DocumentsModal = ({ isOpen, onClose }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [maxAllowed, setMaxAllowed] = useState(5);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchDocuments();
    }
  }, [isOpen]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get("/documents");
      setDocuments(response.data.documents || []);
      setMaxAllowed(response.data.max_allowed || 5);
    } catch (err) {
      console.error("Failed to fetch documents:", err);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'docx', 'txt'].includes(ext)) {
      toast.error("Unsupported file type. Please upload PDF, DOCX, or TXT files.");
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await api.post("/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Document uploaded! Processing...");
      fetchDocuments();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Upload failed";
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (docId, filename) => {
    if (!confirm(`Delete "${filename}"? This cannot be undone.`)) return;

    try {
      await api.delete(`/documents/${docId}`);
      toast.success("Document deleted");
      fetchDocuments();
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "ready":
        return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Ready</span>;
      case "processing":
        return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Processing</span>;
      case "error":
        return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Error</span>;
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            My Documents
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Upload policies, procedures, or regulations for AI-powered analysis. The AI will reference your documents when answering questions.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {/* Upload Section */}
          <div className="border-2 border-dashed border-border rounded-lg p-6 mb-4 text-center hover:border-primary/50 transition-colors">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="document-upload-input"
            />
            <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-foreground mb-1">
              {uploading ? "Uploading..." : "Drop files here or click to upload"}
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              PDF, DOCX, or TXT • Max 10MB
            </p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || documents.length >= maxAllowed}
              data-testid="upload-document-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? "Uploading..." : "Select File"}
            </Button>
            <p className="text-xs text-muted-foreground mt-3">
              {documents.length} of {maxAllowed} documents used
            </p>
          </div>

          {/* Documents List */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8">
              <File className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">No documents uploaded yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Upload your P&P manuals, policies, or regulations to get personalized guidance
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border"
                  data-testid={`document-${doc.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{doc.filename}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{formatFileSize(doc.size_bytes)}</span>
                        <span>•</span>
                        <span>{doc.chunk_count} chunks</span>
                        <span>•</span>
                        {getStatusBadge(doc.status)}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(doc.id, doc.filename)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`delete-doc-${doc.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Footer */}
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
            Documents are processed securely and used only for your personalized guidance. 
            Do not upload documents containing PHI or confidential patient data.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Message Component with Share functionality
const Message = ({ message, isLast, userQuestion, onFollowUp }) => {
  const isUser = message.role === "user";
  const [copied, setCopied] = useState(false);

  // Generate contextual follow-up suggestions based on content
  const getFollowUpSuggestions = () => {
    const content = message.content.toLowerCase();
    const suggestions = [];
    
    if (content.includes("compliance") || content.includes("aaahc") || content.includes("cms")) {
      suggestions.push("How should I document this for our next survey?");
    }
    if (content.includes("staff") || content.includes("personnel") || content.includes("training")) {
      suggestions.push("What's the recommended timeline for implementation?");
    }
    if (content.includes("policy") || content.includes("procedure")) {
      suggestions.push("Can you draft a sample policy template?");
    }
    if (content.includes("risk") || content.includes("liability")) {
      suggestions.push("What are the top 3 priorities to address first?");
    }
    if (content.includes("budget") || content.includes("cost") || content.includes("expense")) {
      suggestions.push("How can I justify this expense to leadership?");
    }
    if (content.includes("patient") || content.includes("safety")) {
      suggestions.push("What metrics should I track for this?");
    }
    
    // Default suggestions if none matched
    if (suggestions.length === 0) {
      suggestions.push("What should I do next?");
      suggestions.push("Can you elaborate on the key points?");
    }
    
    return suggestions.slice(0, 3);
  };

  const handleCopyToClipboard = async () => {
    try {
      const textToCopy = isUser 
        ? message.content 
        : `Question: ${userQuestion}\n\nOpsCounsel Response:\n${message.content}`;
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent("OpsCounsel AI - Operations Guidance");
    const body = encodeURIComponent(
      `Question: ${userQuestion}\n\nOpsCounsel Response:\n${message.content}\n\n---\nGenerated by OpsCounsel AI - Executive Operations Counsel for ASC`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  const handleShareSlack = () => {
    navigator.clipboard.writeText(
      `*OpsCounsel AI Response*\n\n*Question:* ${userQuestion}\n\n*Response:*\n${message.content}`
    );
    toast.success("Formatted for Slack - Paste in your channel");
  };

  return (
    <div
      data-testid={`message-${message.role}`}
      className={`message ${isUser ? "message-user" : "message-assistant"}`}
      style={{ animationDelay: isLast ? "0s" : "0.1s" }}
    >
      <div className="message-content">
        {isUser ? (
          <p className="text-white">{message.content}</p>
        ) : (
          <div className="assistant-response">
            <div className="prose-ops">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
            <div className="share-actions">
              <button
                data-testid="copy-response-btn"
                className="share-btn"
                onClick={handleCopyToClipboard}
                title="Copy to clipboard"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? "Copied" : "Copy"}</span>
              </button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="share-dropdown-btn" className="share-btn">
                    <Share2 className="w-4 h-4" />
                    <span>Share</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-card border-border">
                  <DropdownMenuItem 
                    data-testid="share-email-btn"
                    onClick={handleShareEmail}
                    className="cursor-pointer"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Share via Email
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    data-testid="share-slack-btn"
                    onClick={handleShareSlack}
                    className="cursor-pointer"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Copy for Slack
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Follow-up suggestions - only show on last assistant message */}
            {isLast && onFollowUp && (
              <div className="follow-up-suggestions mt-4 pt-3 border-t border-border/30">
                <p className="text-xs text-muted-foreground mb-2">You might also ask:</p>
                <div className="flex flex-wrap gap-2">
                  {getFollowUpSuggestions().map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => onFollowUp(suggestion)}
                      className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid={`follow-up-${idx}`}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Loading Indicator
const LoadingIndicator = () => (
  <div className="loading-indicator animate-fade-in" data-testid="loading-indicator">
    <div className="loading-dots">
      <span className="loading-dot"></span>
      <span className="loading-dot"></span>
      <span className="loading-dot"></span>
    </div>
    <span className="text-sm">OpsCounsel™ is analyzing...</span>
  </div>
);

// Template Card Component
const TemplateCard = ({ template, onSelect }) => (
  <button
    data-testid={`template-${template.id}`}
    className="template-card text-left"
    onClick={() => onSelect(template.prompt)}
  >
    <div className="template-category">{template.category}</div>
    <div className="template-title">{template.title}</div>
    <div className="template-hint">Click to use template</div>
  </button>
);

// Welcome State Component
const WelcomeState = ({ templates, onSelectTemplate, showTemplates, setShowTemplates, user }) => (
  <div className="welcome-state">
    <img 
      src={LOGO_URL} 
      alt="OpsCounsel™ AI" 
      className="h-24 w-auto object-contain mb-6"
    />
    <p className="welcome-subtitle">
      Executive-level operations counsel for ASC administrators and healthcare leaders. 
      Ask about compliance, staffing, surveys, or select a common scenario below.
    </p>
    
    {user && user.subscription_status === "free" && (
      <div className="mt-4 text-sm text-muted-foreground">
        {7 - user.messages_used} of 7 free messages remaining
      </div>
    )}
    
    <div className="w-full max-w-3xl mt-8">
      <button
        data-testid="toggle-templates-btn"
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mx-auto mb-4"
        onClick={() => setShowTemplates(!showTemplates)}
      >
        <span>Quick Scenarios</span>
        {showTemplates ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {showTemplates && (
        <div className="templates-panel animate-fade-in">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onSelect={onSelectTemplate}
            />
          ))}
        </div>
      )}
    </div>
    
    {/* Social Proof */}
    <SocialProof />
  </div>
);

// Social Proof / Testimonials Component
const SocialProof = () => {
  const testimonials = [
    {
      quote: "OpsCounsel AI helped us prepare for our AAAHC survey in half the time. The compliance guidance is spot-on.",
      author: "Jennifer M.",
      role: "ASC Administrator",
      company: "Midwest Surgical Center"
    },
    {
      quote: "Finally, an AI that understands ASC operations. It's like having a seasoned ops consultant on call 24/7.",
      author: "Dr. Robert K.",
      role: "Medical Director",
      company: "Pacific Coast Surgery"
    },
    {
      quote: "The staffing risk assessment feature alone has saved us from two potential compliance issues.",
      author: "Maria S.",
      role: "Director of Operations",
      company: "Heritage Ambulatory Care"
    }
  ];

  return (
    <div className="social-proof mt-12 pt-8 border-t border-border/50">
      <p className="text-center text-xs uppercase tracking-wider text-muted-foreground mb-6">
        Trusted by healthcare operations leaders
      </p>
      <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
        {testimonials.map((t, idx) => (
          <div key={idx} className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-foreground italic mb-3">"{t.quote}"</p>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{t.author}</p>
                <p className="text-xs text-muted-foreground">{t.role}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// PHI Warning Banner Component
const PHIWarningBanner = () => (
  <div 
    className="w-full max-w-3xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-lg p-4 mt-6"
    data-testid="phi-warning-banner"
  >
    <div className="flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
      <div className="text-sm">
        <p className="font-semibold text-amber-800 dark:text-amber-200 mb-1">
          Important Notice
        </p>
        <p className="text-amber-700 dark:text-amber-300">
          OpsCounsel AI is for general operational guidance only. 
          <strong> Do not enter patient information, PHI, or confidential HR/personal data.</strong> We do not sign BAAs and are not HIPAA-regulated.
        </p>
      </div>
    </div>
  </div>
);

// 22 Operational Domains organized into 4 Pillars (New Structure)
const OPERATIONAL_DOMAINS = {
  governance: {
    name: "Governance & Administration",
    iconType: "Building2",
    bgColor: "bg-blue-50 dark:bg-blue-900/20",
    borderColor: "border-blue-200 dark:border-blue-700",
    iconColor: "text-blue-600 dark:text-blue-400",
    domains: [
      { id: "governance-leadership", name: "Governance & Leadership", prompt: "Help me with governing body structure, bylaws, policies, MEC, and oversight requirements for my ASC." },
      { id: "medical-staff", name: "Medical Staff & Credentialing", prompt: "Guide me through credentialing, privileging, OPPE/FPPE, peer review, and medical staff bylaws." },
      { id: "human-resources", name: "Human Resources", prompt: "Guide personnel files, licensure verification, background checks, health screenings, and performance evaluations." },
      { id: "financial-admin", name: "Financial & Administrative", prompt: "Guide billing oversight, financial controls, charity care policies, and Good Faith Estimates." },
      { id: "ethics-compliance", name: "Ethics & Compliance Culture", prompt: "Help establish code of conduct, non-retaliation policy, reporting mechanisms, and leadership accountability." },
      { id: "contracts-vendors", name: "Contracts & Vendor Oversight", prompt: "Guide me on contracted services agreements, vendor credentialing, performance monitoring, and termination clauses." }
    ]
  },
  clinical: {
    name: "Clinical Operations & Quality",
    iconType: "Stethoscope",
    bgColor: "bg-emerald-50 dark:bg-emerald-900/20",
    borderColor: "border-emerald-200 dark:border-emerald-700",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    domains: [
      { id: "surgical-procedural", name: "Surgical & Procedural Services", prompt: "Help with time-outs, surgical counts, Universal Protocol, procedure documentation, and implant tracking." },
      { id: "anesthesia-services", name: "Anesthesia Services", prompt: "Advise on anesthesia policies, pre/post assessments, provider credentialing, moderate sedation compliance." },
      { id: "nursing-services", name: "Nursing Services", prompt: "Guide nursing scope of practice, staffing plans, competency validation, orientation, and RN documentation." },
      { id: "medication-management", name: "Medication Management", prompt: "Guide me on medication storage, controlled substances, labeling, anesthesia meds, and pharmacy oversight." },
      { id: "infection-prevention", name: "Infection Prevention & Control", prompt: "Help with IC plan, risk assessment, surveillance, hand hygiene, sterilization, and endoscope processing." },
      { id: "qapi", name: "QAPI", prompt: "Help me develop QAPI plan, performance indicators, data collection, action plans, and adverse event trending." },
      { id: "radiology-imaging", name: "Radiology & Imaging", prompt: "Advise on radiation safety, shielding, signage, equipment maintenance, and staff competency." },
      { id: "laboratory-services", name: "Laboratory Services", prompt: "Help with CLIA compliance, point-of-care testing, quality controls, and reference lab agreements." },
      { id: "patient-flow", name: "Patient Flow & Throughput", prompt: "Advise on scheduling, pre-op/PACU workflows, discharge criteria, transfers, and overnight policies." }
    ]
  },
  survey: {
    name: "Survey & Compliance Readiness",
    iconType: "ClipboardCheck",
    bgColor: "bg-violet-50 dark:bg-violet-900/20",
    borderColor: "border-violet-200 dark:border-violet-700",
    iconColor: "text-violet-600 dark:text-violet-400",
    domains: [
      { id: "regulatory-compliance", name: "Regulatory & Compliance Management", prompt: "Help track CMS Conditions for Coverage, state licensure, accreditation standards, and policy management." },
      { id: "readiness-infrastructure", name: "Readiness Infrastructure", prompt: "Help with mock surveys, tracer methodology, staff interview readiness, document control, and survey day command." },
      { id: "patient-rights", name: "Patient Rights & Grievances", prompt: "Help with patient rights policy, grievance process, informed consent, HIPAA, and complaint handling." },
      { id: "information-management", name: "Information Management & Medical Records", prompt: "Help with medical records, documentation completeness, retention, EHR access controls, and data security." }
    ]
  },
  safety: {
    name: "Environment, Risk & Safety",
    iconType: "ShieldCheck",
    bgColor: "bg-amber-50 dark:bg-amber-900/20",
    borderColor: "border-amber-200 dark:border-amber-700",
    iconColor: "text-amber-600 dark:text-amber-400",
    domains: [
      { id: "environment-of-care", name: "Environment of Care & Life Safety", prompt: "Help with life safety, emergency lighting, fire drills, medical gas, utilities, temp/humidity logs, safety rounds." },
      { id: "emergency-preparedness", name: "Emergency Preparedness", prompt: "Guide emergency operations plan, HVA, disaster drills, evacuation, transfer agreements, communication plans." },
      { id: "risk-management", name: "Risk Management & Incident Reporting", prompt: "Advise on incident reporting, sentinel events, RCA, follow-up/mitigation, and governing body notification." },
      { id: "facilities-equipment", name: "Facilities, Equipment & Biomedical", prompt: "Advise on preventive maintenance, equipment inventories, recall management, biomedical inspections, calibration." }
    ]
  }
};

// Pillar Icon Component
const PillarIcon = ({ type, className }) => {
  const icons = {
    Building2: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/>
        <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/>
        <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/>
        <path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>
      </svg>
    ),
    Stethoscope: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3"/>
        <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4"/>
        <circle cx="20" cy="10" r="2"/>
      </svg>
    ),
    ClipboardCheck: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
        <path d="m9 14 2 2 4-4"/>
      </svg>
    ),
    ShieldCheck: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
        <path d="m9 12 2 2 4-4"/>
      </svg>
    )
  };
  return icons[type] || null;
};

// Domain Navigator Component
const DomainNavigator = ({ onSelectDomain }) => {
  const [expandedPillar, setExpandedPillar] = useState(null);

  const pillars = Object.entries(OPERATIONAL_DOMAINS);

  return (
    <div className="w-full max-w-4xl mt-8" data-testid="domain-navigator">
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold text-foreground mb-1">23 Operational Domains</h2>
        <p className="text-sm text-muted-foreground">Select a domain for guidance crosswalked across AAAHC, CMS & Joint Commission</p>
      </div>
      
      {/* 4-Pillar Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {pillars.map(([key, pillar]) => (
          <button
            key={key}
            data-testid={`pillar-${key}`}
            onClick={() => setExpandedPillar(expandedPillar === key ? null : key)}
            className={`
              relative p-4 rounded-xl border-2 transition-all duration-200
              ${expandedPillar === key 
                ? `${pillar.bgColor} ${pillar.borderColor} shadow-lg scale-[1.02]` 
                : 'bg-card border-border hover:border-primary/50 hover:shadow-md'}
            `}
          >
            <PillarIcon type={pillar.iconType} className={`w-8 h-8 mb-2 ${pillar.iconColor}`} />
            <h3 className="text-sm font-semibold text-foreground leading-tight">{pillar.name}</h3>
            <p className="text-xs text-muted-foreground mt-1">{pillar.domains.length} domains</p>
            <ChevronDown 
              className={`absolute bottom-2 right-2 w-4 h-4 text-muted-foreground transition-transform ${
                expandedPillar === key ? 'rotate-180' : ''
              }`} 
            />
          </button>
        ))}
      </div>

      {/* Expanded Domain Chips */}
      {expandedPillar && (
        <div 
          className={`
            p-4 rounded-xl border animate-fade-in
            ${OPERATIONAL_DOMAINS[expandedPillar].bgColor}
            ${OPERATIONAL_DOMAINS[expandedPillar].borderColor}
          `}
          data-testid="domain-chips-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">{OPERATIONAL_DOMAINS[expandedPillar].icon}</span>
            <h4 className="font-semibold text-foreground">{OPERATIONAL_DOMAINS[expandedPillar].name}</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {OPERATIONAL_DOMAINS[expandedPillar].domains.map((domain) => (
              <button
                key={domain.id}
                data-testid={`domain-chip-${domain.id}`}
                onClick={() => onSelectDomain(domain)}
                className="
                  px-3 py-1.5 text-sm rounded-full
                  bg-white dark:bg-gray-800 
                  border border-gray-200 dark:border-gray-600
                  text-foreground
                  hover:bg-primary hover:text-white hover:border-primary
                  transition-all duration-150
                  shadow-sm hover:shadow
                "
              >
                {domain.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="flex justify-center gap-6 mt-6 text-xs text-muted-foreground">
        <span>✓ AAAHC Standards</span>
        <span>✓ CMS CoP</span>
        <span>✓ Joint Commission</span>
      </div>
    </div>
  );
};

// Welcome State with Pricing Link
const WelcomeStateWithPricing = ({ templates, onSelectTemplate, showTemplates, setShowTemplates, user, onViewPricing, guestMessagesUsed, guestLimit, onSelectDomain, onLaunchCounsel }) => {
  const [showLegacyTemplates, setShowLegacyTemplates] = useState(false);
  
  return (
    <div className="welcome-state">
      <img 
        src={LOGO_URL} 
        alt="OpsCounsel™ AI" 
        className="h-20 w-auto object-contain mb-4"
      />
      <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
        Executive-level operations counsel for ASC administrators and healthcare leaders.
      </h1>
      <p className="welcome-subtitle text-base text-muted-foreground max-w-2xl">
        24/7 decision support across 23 operational domains. Expert guidance crosswalked across AAAHC, CMS, and Joint Commission standards.
      </p>
      
      {/* Primary CTA Button */}
      <div className="mt-6">
        <Button
          size="lg"
          onClick={onLaunchCounsel}
          className="px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
          data-testid="launch-counsel-btn"
        >
          <MessageSquare className="w-5 h-5 mr-2" />
          Launch 24/7 Counsel
        </Button>
        
        {/* Assessment CTA */}
        <a
          href="https://key-assets.com/readiness-assessment"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-3 px-6 py-3 text-sm font-medium border-2 border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-all"
          data-testid="assessment-cta"
        >
          Take 6-Min Assessment
        </a>
      </div>
      
      {user && user.subscription_status === "free" && (
        <div className="mt-4 text-sm text-muted-foreground">
          {7 - user.messages_used} of 7 free messages remaining
          <button 
            onClick={onViewPricing}
            className="ml-2 text-primary hover:underline"
            data-testid="view-pricing-link"
          >
            View pricing
          </button>
        </div>
      )}
      
      {!user && (
        <div className="mt-4 text-sm">
          <span className="text-muted-foreground">
            Try {guestLimit - guestMessagesUsed} preview messages free • 
          </span>
          <button 
            onClick={onViewPricing}
            className="ml-1 text-primary hover:underline"
            data-testid="view-pricing-link"
          >
            View pricing
          </button>
        </div>
      )}
      
      {/* Domain Navigator - Always Visible */}
      <DomainNavigator onSelectDomain={onSelectDomain} />
      
      {/* Legacy Quick Scenarios - Hidden by default */}
      <div className="w-full max-w-3xl mt-6">
        <button
          data-testid="toggle-templates-btn"
          onClick={() => setShowLegacyTemplates(!showLegacyTemplates)}
          className="px-4 py-2 text-sm border border-border rounded-lg text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all"
        >
          {showLegacyTemplates ? "Hide Quick Scenarios" : "Browse Quick Scenarios"}
        </button>
        
        {showLegacyTemplates && (
          <div className="templates-panel animate-fade-in mt-4">
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onSelect={onSelectTemplate}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Social Proof */}
      <SocialProof />
    </div>
  );
};

// Payment Success/Cancel Pages
const PaymentSuccess = ({ onComplete }) => {
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    const checkPayment = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("session_id");
      
      if (!sessionId) {
        setStatus("error");
        return;
      }

      // Poll for payment status
      let attempts = 0;
      const maxAttempts = 5;
      
      const poll = async () => {
        try {
          const response = await api.get(`/subscription/checkout/status/${sessionId}`);
          if (response.data.status === "paid") {
            setStatus("success");
            setTimeout(() => {
              window.history.replaceState({}, "", "/");
              onComplete();
            }, 2000);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 2000);
          } else {
            setStatus("pending");
          }
        } catch (err) {
          setStatus("error");
        }
      };
      
      poll();
    };
    
    checkPayment();
  }, [onComplete]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center bg-[#F6F7F9]">
      <img 
        src={LOGO_URL} 
        alt="OpsCounsel™ AI" 
        className="h-20 w-auto object-contain mb-6"
      />
      <h1 className="text-2xl font-bold text-[#0B2A4A] mb-2">
        {status === "checking" && "Processing Payment..."}
        {status === "success" && "Payment Successful"}
        {status === "error" && "Payment Error"}
        {status === "pending" && "Payment Processing"}
      </h1>
      <p className="text-muted-foreground">
        {status === "checking" && "Please wait while we confirm your payment."}
        {status === "success" && "Your subscription is now active. Redirecting..."}
        {status === "error" && "There was an issue with your payment. Please try again."}
        {status === "pending" && "Your payment is being processed. Please check back shortly."}
      </p>
      
      {/* Expense Justification - show on success */}
      {status === "success" && (
        <div className="mt-8 p-4 bg-muted/30 rounded-lg">
          <p className="text-sm text-muted-foreground mb-2">Need to submit this for reimbursement?</p>
          <a 
            href={EXPENSE_JUSTIFICATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
            data-testid="success-expense-link"
          >
            <FileText className="w-4 h-4" />
            Download the 1-page expense justification
          </a>
        </div>
      )}
    </div>
  );
};

const PaymentCancel = ({ onReturn }) => (
  <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center bg-[#F6F7F9]">
    <img 
      src={LOGO_URL} 
      alt="OpsCounsel™ AI" 
      className="h-20 w-auto object-contain mb-6"
    />
    <h1 className="text-2xl font-bold text-[#0B2A4A] mb-2">Payment Cancelled</h1>
    <p className="text-muted-foreground mb-6">No charges were made to your account.</p>
    <Button onClick={onReturn} data-testid="return-btn">Return to OpsCounsel</Button>
  </div>
);

// ============ Main App ============

function App() {
  // Auth state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showPortal, setShowPortal] = useState(false);
  const [showGuestSignupPrompt, setShowGuestSignupPrompt] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  
  // Guest message tracking (stored in localStorage)
  const [guestMessagesUsed, setGuestMessagesUsed] = useState(() => {
    const stored = localStorage.getItem("guestMessagesUsed");
    return stored ? parseInt(stored, 10) : 0;
  });
  const GUEST_MESSAGE_LIMIT = 3;
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(true);
  const [currentPage, setCurrentPage] = useState("chat");
  
  // Conversation/History state
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem("darkMode");
    return stored === "true";
  });
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Check for payment routes and pricing page
  const isPaymentSuccess = window.location.pathname === "/payment/success";
  const isPaymentCancel = window.location.pathname === "/payment/cancel";
  const isPricingPage = window.location.pathname === "/pricing";
  const isAdminPage = window.location.pathname === "/admin";
  const isTermsPage = window.location.pathname === "/terms";
  const isPrivacyPage = window.location.pathname === "/privacy";

  // Set initial page based on URL
  useEffect(() => {
    if (isPricingPage) {
      setCurrentPage("pricing");
    } else if (isAdminPage) {
      setCurrentPage("admin");
    } else if (isTermsPage) {
      setCurrentPage("terms");
    } else if (isPrivacyPage) {
      setCurrentPage("privacy");
    }
  }, [isPricingPage, isAdminPage, isTermsPage, isPrivacyPage]);

  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await api.get("/auth/me");
          setUser(response.data);
        } catch (err) {
          localStorage.removeItem("token");
        }
      }
      setAuthLoading(false);
    };
    checkAuth();
  }, []);

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API}/templates`);
        setTemplates(response.data);
      } catch (error) {
        console.error("Failed to fetch templates:", error);
      }
    };
    fetchTemplates();
  }, []);

  // Dark mode effect
  useEffect(() => {
    localStorage.setItem("darkMode", darkMode);
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  // Load conversations when user logs in
  useEffect(() => {
    const loadConversations = async () => {
      if (user) {
        try {
          const response = await api.get("/conversations");
          setConversations(response.data.conversations || []);
        } catch (err) {
          console.error("Failed to load conversations:", err);
        }
      }
    };
    loadConversations();
  }, [user]);

  // Refresh user data
  const refreshUser = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (err) {
      console.error("Failed to refresh user:", err);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setMessages([]);
    setConversations([]);
    setCurrentConversationId(null);
    toast.success("Signed out successfully");
  };

  // Conversation management functions
  const createNewConversation = async () => {
    if (!user) return;
    try {
      const response = await api.post("/conversations", { title: "New Conversation" });
      const newConv = { id: response.data.id, title: response.data.title, messages: [], created_at: new Date().toISOString() };
      setConversations(prev => [newConv, ...prev]);
      setCurrentConversationId(response.data.id);
      setMessages([]);
    } catch (err) {
      console.error("Failed to create conversation:", err);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await api.get(`/conversations/${conversationId}`);
      setCurrentConversationId(conversationId);
      setMessages(response.data.messages || []);
      setShowHistory(false);
    } catch (err) {
      console.error("Failed to load conversation:", err);
    }
  };

  const deleteConversation = async (conversationId) => {
    try {
      await api.delete(`/conversations/${conversationId}`);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (currentConversationId === conversationId) {
        setCurrentConversationId(null);
        setMessages([]);
      }
      toast.success("Conversation deleted");
    } catch (err) {
      console.error("Failed to delete conversation:", err);
    }
  };

  const saveMessageToConversation = async (message) => {
    if (!user || !currentConversationId) return;
    try {
      await api.post(`/conversations/${currentConversationId}/messages`, message);
    } catch (err) {
      console.error("Failed to save message:", err);
    }
  };

  // Export conversation as PDF
  const exportConversationPDF = () => {
    if (messages.length === 0) {
      toast.error("No messages to export");
      return;
    }
    
    // Create printable content
    const content = messages.map(m => 
      `${m.role === 'user' ? 'You' : 'OpsCounsel AI'}:\n${m.content}\n`
    ).join('\n---\n\n');
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>OpsCounsel AI - Conversation Export</title>
          <style>
            body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.6; }
            h1 { color: #0B2A4A; border-bottom: 2px solid #C9A24D; padding-bottom: 10px; }
            .message { margin: 20px 0; padding: 15px; border-radius: 8px; }
            .user { background: #f0f0f0; }
            .assistant { background: #fff; border: 1px solid #E1E4E8; }
            .role { font-weight: bold; color: #0B2A4A; margin-bottom: 8px; }
            hr { border: none; border-top: 1px solid #E1E4E8; margin: 20px 0; }
            .footer { margin-top: 40px; font-size: 12px; color: #666; text-align: center; }
          </style>
        </head>
        <body>
          <h1>OpsCounsel™ AI - Conversation Export</h1>
          <p style="color: #666;">Exported on ${new Date().toLocaleDateString()}</p>
          ${messages.map(m => `
            <div class="message ${m.role}">
              <div class="role">${m.role === 'user' ? 'You' : 'OpsCounsel AI'}</div>
              <div>${m.content.replace(/\n/g, '<br>')}</div>
            </div>
          `).join('<hr>')}
          <div class="footer">
            Generated by OpsCounsel™ AI - Executive Operations Counsel for ASC Leaders
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Handle sending a message
  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    // If user is not authenticated, use guest chat
    if (!user) {
      // Check if guest has used all free messages
      if (guestMessagesUsed >= GUEST_MESSAGE_LIMIT) {
        setShowGuestSignupPrompt(true);
        return;
      }

      const userMessage = { role: "user", content: input.trim() };
      setMessages((prev) => [...prev, userMessage]);
      setInput("");
      setIsLoading(true);

      try {
        const response = await axios.post(`${API}/chat/guest`, {
          message: userMessage.content,
          history: messages,
          guest_messages_used: guestMessagesUsed,
        });

        const assistantMessage = {
          role: "assistant",
          content: response.data.response,
        };
        setMessages((prev) => [...prev, assistantMessage]);
        
        // Update guest message count
        const newCount = response.data.guest_messages_used;
        setGuestMessagesUsed(newCount);
        localStorage.setItem("guestMessagesUsed", newCount.toString());

        // Check if guest should sign up
        if (response.data.should_signup) {
          setTimeout(() => {
            setShowGuestSignupPrompt(true);
          }, 1500);
        }
      } catch (error) {
        console.error("Guest chat error:", error);
        
        if (error.response?.status === 403 && error.response?.data?.detail?.code === "GUEST_LIMIT_REACHED") {
          setShowGuestSignupPrompt(true);
          setMessages((prev) => prev.slice(0, -1));
        } else {
          toast.error("Failed to get response. Please try again.");
          setMessages((prev) => prev.slice(0, -1));
        }
      } finally {
        setIsLoading(false);
        inputRef.current?.focus();
      }
      return;
    }

    // Check if user has messages remaining (authenticated users)
    if (user.subscription_status === "free" && user.messages_used >= 7) {
      setShowPaywall(true);
      return;
    }

    // Create new conversation if none exists
    if (!currentConversationId) {
      try {
        const convResponse = await api.post("/conversations", { title: "New Conversation" });
        setCurrentConversationId(convResponse.data.id);
        setConversations(prev => [{ id: convResponse.data.id, title: convResponse.data.title, messages: [], created_at: new Date().toISOString() }, ...prev]);
      } catch (err) {
        console.error("Failed to create conversation:", err);
      }
    }

    const userMessage = { role: "user", content: input.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    // Save user message to conversation
    if (currentConversationId) {
      saveMessageToConversation(userMessage);
    }

    try {
      const response = await api.post("/chat", {
        message: userMessage.content,
        history: messages,
      });

      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
      };
      setMessages((prev) => [...prev, assistantMessage]);
      
      // Save assistant message to conversation
      if (currentConversationId) {
        saveMessageToConversation(assistantMessage);
      }
      
      // Update user state with new message count
      setUser(prev => ({
        ...prev,
        messages_used: response.data.messages_used,
        messages_remaining: response.data.messages_remaining
      }));

      // Check if limit reached
      if (response.data.is_limited) {
        setShowPaywall(true);
      }
    } catch (error) {
      console.error("Chat error:", error);
      
      if (error.response?.status === 403 && error.response?.data?.detail?.code === "LIMIT_REACHED") {
        setShowPaywall(true);
        setMessages((prev) => prev.slice(0, -1));
      } else if (error.response?.status === 401) {
        setShowAuthModal(true);
        setMessages((prev) => prev.slice(0, -1));
      } else {
        toast.error("Failed to get response. Please try again.");
        setMessages((prev) => prev.slice(0, -1));
      }
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  // Handle plan selection
  const handleSelectPlan = async (planId) => {
    try {
      const response = await api.post("/subscription/checkout", {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (err) {
      toast.error("Failed to start checkout. Please try again.");
    }
  };

  // Handle template selection
  const handleSelectTemplate = (prompt) => {
    setInput(prompt);
    setShowTemplates(false);
    inputRef.current?.focus();
  };

  // Handle domain selection from Domain Navigator
  const handleSelectDomain = (domain) => {
    setInput(domain.prompt);
    inputRef.current?.focus();
    // Auto-submit after a brief delay for better UX
    setTimeout(() => {
      handleSend();
    }, 100);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
  };

  // Handle payment completion
  const handlePaymentComplete = () => {
    refreshUser();
    window.history.replaceState({}, "", "/");
    setCurrentPage("chat");
  };

  // Handle pricing page navigation
  const handlePricingBack = (action) => {
    if (action === "auth") {
      setShowAuthModal(true);
    }
    window.history.replaceState({}, "", "/");
    setCurrentPage("chat");
  };

  // Navigate to pricing
  const goToPricing = () => {
    window.history.pushState({}, "", "/pricing");
    setCurrentPage("pricing");
  };

  // Navigate to admin
  const goToAdmin = () => {
    window.history.pushState({}, "", "/admin");
    setCurrentPage("admin");
  };

  // Handle admin back
  const handleAdminBack = () => {
    window.history.replaceState({}, "", "/");
    setCurrentPage("chat");
  };

  // Show payment pages
  if (isPaymentSuccess) {
    return (
      <div className="app-container" data-testid="payment-success">
        <Toaster position="top-center" theme="dark" />
        <PaymentSuccess onComplete={handlePaymentComplete} />
      </div>
    );
  }

  if (isPaymentCancel) {
    return (
      <div className="app-container" data-testid="payment-cancel">
        <Toaster position="top-center" theme="dark" />
        <PaymentCancel onReturn={() => {
          window.history.replaceState({}, "", "/");
          setCurrentPage("chat");
          window.location.reload();
        }} />
      </div>
    );
  }

  // Show admin dashboard
  if (currentPage === "admin") {
    return (
      <div data-testid="admin-page">
        <Toaster position="top-center" theme="dark" />
        <AdminDashboard 
          onBack={handleAdminBack}
          user={user}
        />
      </div>
    );
  }

  // Show pricing page
  if (currentPage === "pricing") {
    return (
      <div data-testid="pricing-page">
        <Toaster position="top-center" theme="dark" />
        <PricingPage 
          onBack={handlePricingBack}
          onSelectPlan={handleSelectPlan}
          user={user}
        />
      </div>
    );
  }

  // Show terms page
  if (currentPage === "terms") {
    return (
      <div data-testid="terms-page">
        <Toaster position="top-center" theme="dark" />
        <TermsPage 
          onBack={() => {
            window.history.replaceState({}, "", "/");
            setCurrentPage("chat");
          }}
        />
      </div>
    );
  }

  // Show privacy page
  if (currentPage === "privacy") {
    return (
      <div data-testid="privacy-page">
        <Toaster position="top-center" theme="dark" />
        <PrivacyPage 
          onBack={() => {
            window.history.replaceState({}, "", "/");
            setCurrentPage("chat");
          }}
        />
      </div>
    );
  }

  // Loading state
  if (authLoading) {
    return (
      <div className="app-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <div className="app-container" data-testid="app-container">
        <Toaster position="top-center" theme="dark" />
        
        {/* Auth Modal */}
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)}
          onSuccess={(userData) => {
            setUser(userData);
            toast.success("Welcome to OpsCounsel™ AI");
          }}
        />

        {/* Paywall Modal */}
        <PaywallModal
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          onSelectPlan={handleSelectPlan}
        />

        {/* Guest Signup Prompt */}
        <GuestSignupPrompt
          isOpen={showGuestSignupPrompt}
          onClose={() => setShowGuestSignupPrompt(false)}
          onSignup={() => {
            setShowGuestSignupPrompt(false);
            setShowAuthModal(true);
          }}
          guestMessagesUsed={guestMessagesUsed}
          onEmailCapture={async (email) => {
            await axios.post(`${API}/leads/capture`, { email, source: "guest_signup_prompt" });
          }}
        />

        {/* Customer Portal */}
        <CustomerPortal
          isOpen={showPortal}
          onClose={() => setShowPortal(false)}
          onUpgrade={handleSelectPlan}
          onRefreshUser={refreshUser}
        />

        {/* Documents Modal */}
        <DocumentsModal
          isOpen={showDocuments}
          onClose={() => setShowDocuments(false)}
        />
        
        {/* History Sidebar */}
        {showHistory && user && (
          <div className="fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowHistory(false)} />
            <div className="relative w-80 bg-card border-r border-border h-full overflow-hidden flex flex-col">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="font-semibold text-foreground">Chat History</h2>
                <button onClick={() => setShowHistory(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-3">
                <Button 
                  className="w-full" 
                  onClick={() => { createNewConversation(); setShowHistory(false); }}
                  data-testid="new-conversation-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> New Conversation
                </Button>
              </div>
              <ScrollArea className="flex-1 px-3">
                {conversations.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No conversations yet</p>
                ) : (
                  <div className="space-y-2 pb-4">
                    {conversations.map((conv) => (
                      <div
                        key={conv.id}
                        className={`group p-3 rounded-lg cursor-pointer transition-colors ${
                          currentConversationId === conv.id 
                            ? "bg-primary/10 border border-primary/30" 
                            : "bg-muted/50 hover:bg-muted"
                        }`}
                        onClick={() => loadConversation(conv.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{conv.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(conv.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteConversation(conv.id); }}
                            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        )}
        
        {/* Header */}
        <header className="app-header" data-testid="app-header">
          <div className="max-w-[900px] mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {user && (
                <button
                  onClick={() => setShowHistory(true)}
                  className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="history-btn"
                  title="Chat History"
                >
                  <History className="w-5 h-5" />
                </button>
              )}
              <img 
                src={LOGO_URL} 
                alt="OpsCounsel™ AI" 
                className="h-10 w-auto object-contain"
              />
            </div>
            
            <div className="flex items-center gap-2">
              {/* Export PDF button */}
              {messages.length > 0 && (
                <button
                  onClick={exportConversationPDF}
                  className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                  title="Export as PDF"
                  data-testid="export-pdf-btn"
                >
                  <Download className="w-5 h-5" />
                </button>
              )}
              
              {/* Dark mode toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                title={darkMode ? "Light Mode" : "Dark Mode"}
                data-testid="dark-mode-toggle"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              
              <button
                onClick={goToPricing}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden md:block"
                data-testid="pricing-nav-link"
              >
                Pricing
              </button>
              {user ? (
                <UserMenu 
                  user={user} 
                  onLogout={handleLogout}
                  onOpenPortal={() => setShowPortal(true)}
                  onOpenAdmin={goToAdmin}
                  onOpenDocuments={() => setShowDocuments(true)}
                />
              ) : (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAuthModal(true)}
                  data-testid="signin-btn"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
          
          {user ? (
            <MessageCounter 
              used={user.messages_used} 
              remaining={7 - user.messages_used}
              status={user.subscription_status}
            />
          ) : (
            <GuestMessageCounter 
              guestMessagesUsed={guestMessagesUsed}
              limit={GUEST_MESSAGE_LIMIT}
            />
          )}
        </header>

        {/* Main Chat Area */}
        <main className="chat-container">
          {messages.length === 0 ? (
            <WelcomeStateWithPricing
              templates={templates}
              onSelectTemplate={handleSelectTemplate}
              showTemplates={showTemplates}
              setShowTemplates={setShowTemplates}
              user={user}
              onViewPricing={goToPricing}
              guestMessagesUsed={guestMessagesUsed}
              guestLimit={GUEST_MESSAGE_LIMIT}
              onSelectDomain={handleSelectDomain}
              onLaunchCounsel={() => inputRef.current?.focus()}
            />
          ) : (
            <ScrollArea className="messages-area" data-testid="messages-area">
              {messages.map((message, index) => {
                const userQuestion = message.role === "assistant" && index > 0
                  ? messages[index - 1]?.content
                  : "";
                return (
                  <Message
                    key={index}
                    message={message}
                    isLast={index === messages.length - 1}
                    userQuestion={userQuestion}
                    onFollowUp={(suggestion) => {
                      setInput(suggestion);
                      inputRef.current?.focus();
                    }}
                  />
                );
              })}
              {isLoading && <LoadingIndicator />}
              <div ref={messagesEndRef} />
            </ScrollArea>
          )}
        </main>

        {/* Input Area */}
        <div className="input-area" data-testid="input-area">
          <div className="max-w-[900px] mx-auto">
            {messages.length > 0 && templates.length > 0 && (
              <button
                data-testid="show-templates-btn"
                className="text-xs text-muted-foreground hover:text-foreground mb-2 transition-colors"
                onClick={() => setShowTemplates(!showTemplates)}
              >
                {showTemplates ? "Hide scenarios" : "Show quick scenarios"}
              </button>
            )}
            
            {messages.length > 0 && showTemplates && templates.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-3 mb-3 scrollbar-hide">
                {templates.slice(0, 5).map((template) => (
                  <button
                    key={template.id}
                    data-testid={`quick-template-${template.id}`}
                    className="flex-shrink-0 text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                    onClick={() => handleSelectTemplate(template.prompt)}
                  >
                    {template.title}
                  </button>
                ))}
              </div>
            )}
            
            {/* Important Notice - Above Chat Input */}
            <div className="w-full mb-3 px-2">
              <p className="text-xs text-amber-600 dark:text-amber-400 text-center flex items-center justify-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  <strong>Important:</strong> Do not enter patient information, PHI, or confidential HR data. We do not sign BAAs.
                </span>
              </p>
            </div>
            
            <div className="input-wrapper">
              <textarea
                ref={inputRef}
                data-testid="chat-input"
                className="chat-input"
                placeholder="Ask about compliance, staffing, surveys, or any ASC operational challenge..."
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                disabled={isLoading}
                rows={1}
              />
              <Button
                data-testid="send-button"
                className="send-button"
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground mt-3 text-center">
              OpsCounsel™ AI provides operational best-practice guidance and decision support only. 
              It does not provide legal, medical, or HR advice and does not replace professional counsel or regulatory guidance.
            </p>
            
            {/* KARG Handoff CTA - Persistent Footer */}
            <p className="text-xs text-muted-foreground mt-2 text-center">
              For hands-on survey prep or crisis support:{" "}
              <a 
                href="https://key-assets.com/inquiry" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary/80 hover:text-primary hover:underline"
              >
                KARG Consulting →
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-card border-t border-border py-4 px-4" data-testid="app-footer">
          <div className="max-w-[900px] mx-auto">
            {/* KARG Ecosystem Links */}
            <div className="flex flex-wrap items-center justify-center gap-4 mb-4 pb-4 border-b border-border">
              <span className="text-xs text-muted-foreground font-medium">KARG Ecosystem:</span>
              <a 
                href="https://key-assets.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                KARG Consulting
              </a>
              <span className="text-muted-foreground">•</span>
              <a 
                href="https://key-assets.com/readiness-assessment" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                6-Min Readiness Assessment
              </a>
              <span className="text-muted-foreground">•</span>
              <a 
                href="https://ops.key-assets.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                KARG Operator Portal
              </a>
            </div>
            
            {/* Copyright & Legal */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>© {new Date().getFullYear()} OpsCounsel™ AI. A KARG Product.</span>
              <div className="flex items-center gap-4">
                <a 
                  href="/terms" 
                  className="hover:text-foreground transition-colors"
                  onClick={(e) => { e.preventDefault(); window.location.href = '/terms'; }}
                  data-testid="terms-link"
                >
                  Terms of Service
                </a>
                <a 
                  href="/privacy" 
                  className="hover:text-foreground transition-colors"
                  onClick={(e) => { e.preventDefault(); window.location.href = '/privacy'; }}
                  data-testid="privacy-link"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </AuthContext.Provider>
  );
}

export default App;
